import React from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import BedIcon from "@mui/icons-material/Bed";
import BathtubIcon from "@mui/icons-material/Bathtub";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import HomeIcon from "@mui/icons-material/Home";
import EditIcon from "@mui/icons-material/Edit";
import MapIcon from "@mui/icons-material/Map";
import ErrorIcon from "@mui/icons-material/Error";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import styles from "./DashListingDetails.module.css"; // Import CSS module for styling
import Map from "../../../Map/Map"; // Import Map component
import ListingImages from "../../../../Views/ListingDetails/ListingImages/ListingImages";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: "1000px",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "15px",
  maxHeight: "90vh",
};

function DashListingDetails({ listing, detailsModel, closeModel }) {
  const navigate = useNavigate(); // Initialize useNavigate hook

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  // Navigate to edit component when pencil icon is clicked
  const handleEditClick = () => {
    navigate(`/edit-listing/${listing.url}`); // Redirect to the edit page with listing id
  };

  return (
    <Modal
      open={detailsModel}
      onClose={closeModel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyle} className={styles.container}>
        <Paper elevation={3} className={styles.listingDetailsContainer}>
          {/* Header */}
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            className={styles.header}
          >
            <Typography variant="h4" gutterBottom className={styles.title}>
              {listing.title}
            </Typography>
            <div className={styles.editIcons}>
              {/* Pencil icon that redirects to the edit component */}
              <IconButton onClick={handleEditClick}>
                <EditIcon />
              </IconButton>
            </div>
          </Grid>

          {/* Location */}
          <Chip
            icon={<LocationOnIcon />}
            label={`${listing.city}, ${listing.state} ${listing.zipcode}`}
            className={styles.locationChip}
          />

          {/* Property Status */}
          <Chip
            label={listing.status || "Available"} // Default to "Available" if status is not provided
            color={
              listing.status === "Sold"
                ? "error"
                : listing.status === "Off Market"
                ? "warning"
                : "success"
            }
            className={styles.statusChip}
          />

          {/* Image Section */}
          {/* <div className={styles.listingImagePlaceholder}>
            <img
              src={listing.image_url}
              alt="Listing"
              className={styles.listingImage}
            />
          </div> */}

          <ListingImages listing={listing} />

          {/* Property Info */}
          <Grid container spacing={3} className={styles.listingDetailsGrid}>
            <Grid item xs={12} md={8}>
              <div className={styles.listingPriceDetails}>
                <Typography variant="h5" gutterBottom>
                  <LocalOfferIcon />{" "}
                  {listing.listing_type === "2" ? "For Sale" : "For Rent"}
                </Typography>
                <Typography variant="h4" color="primary">
                  {listing.listing_type === "2"
                    ? formatPrice(listing.sell_price)
                    : `${formatPrice(listing.rent_price)}/month`}
                </Typography>

                {listing.listing_type === "2" && (
                  <Typography>
                    <strong>HOA Fee:</strong>{" "}
                    {formatPrice(listing.sell_hoa_fee)}/month
                  </Typography>
                )}
                {listing.listing_type === "1" && listing.rent_lease_length && (
                  <Typography>
                    <strong>Lease Length:</strong> {listing.rent_lease_length}{" "}
                    Months
                  </Typography>
                )}
                {listing.listing_type === "1" && listing.rent_pet_policy && (
                  <Typography>
                    <strong>Pet Policy:</strong> {listing.rent_pet_policy}
                  </Typography>
                )}
                <Typography>
                  <strong>Available From:</strong>{" "}
                  {new Date(listing.available_from).toLocaleDateString()}
                </Typography>
              </div>

              <div className={styles.listingQuickFacts}>
                <Typography variant="h6" gutterBottom>
                  Quick Facts
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Chip
                      icon={<BedIcon />}
                      label={`${listing.bedrooms} Beds`}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Chip
                      icon={<BathtubIcon />}
                      label={`${listing.bathrooms} Baths`}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Chip
                      icon={<SquareFootIcon />}
                      label={`${listing.area_sqft} sqft`}
                    />
                  </Grid>
                </Grid>
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper elevation={2} className={styles.listingFormDetails}>
                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
                <Typography paragraph>
                  {listing.description || "No description available"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Property Details */}
          <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
            Property Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography>
                <strong>Heating/Cooling:</strong> {listing.heating_cooling}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>
                <strong>Landmark:</strong> {listing.landmark}
              </Typography>
            </Grid>
            {!listing.hide_address && (
              <Grid item xs={12}>
                <Typography>
                  <strong>Address:</strong> {listing.street}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Map Section */}
          <div className={styles.sectionTitle}>
            <Typography variant="h6" gutterBottom>
              Location
            </Typography>
          </div>
          <div className={styles.mapSection}>
            <div className={styles.mapSectionHeader}>
              <MapIcon />
              <div className={styles.mapSectionTitle}>Address</div>
            </div>

            <div className={styles.mapContainer}>
              <Map
                propertyData={{
                  street: listing.street || "",
                  Unit: listing.street_2 || "",
                  city: listing.city || "",
                  state: listing.state || "",
                  zipcode: listing.zipcode || "",
                  address: `${listing.street || ""} ${
                    listing.street_2 || ""
                  }, ${listing.city || ""}, ${listing.state || ""} ${
                    listing.zipcode || ""
                  }`,
                }}
              />
            </div>

            {/* {listing.hide_address !== true ? (
              <div className={styles.mapContainer}>
                <Map propertyData={{
                  street: listing.street || "",
                  Unit : listing.street_2 || "",
                  city: listing.city || "",
                  state: listing.state || "",
                  zipcode: listing.zipcode || "",
                  address: `${listing.street || ""} ${listing.street_2 || ""}, ${listing.city || ""}, ${listing.state || ""} ${listing.zipcode || ""}`
                }} />
              </div>
            ) : (
              <div className={styles.addressCaution}>
                <ErrorIcon className={styles.icon} />
                <div className={styles.text}>
                  Owner/Agent has opted to hide the exact address!
                </div>
              </div>
            )} */}
          </div>
        </Paper>
      </Box>
    </Modal>
  );
}

export default DashListingDetails;
